import { useAuth0 } from "@auth0/auth0-react";
import { SSESubscriber } from "@portal/components/lib/SSESubscriber";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { getConfig } from "../../config";
import { isTerminalAuthenticationError } from "../../utils/request";
import { useFeatureFlags } from "../feature-flags";
import { SseSessionEndedError } from "./SseSessionEndedError";

const BATCH_DEBOUNCE_MS = 300;
const MAX_BATCH_WAIT_MS = 5_000;

export function ServerSentEvents() {
	const { getAccessTokenSilently } = useAuth0();
	const { user } = useAuthenticatedUser();
	const queryClient = useQueryClient();
	const { featureFlags } = useFeatureFlags();
	const config = getConfig();

	const sseEnabled = featureFlags?.find((f) => f.name === "sse_enabled")?.enabled ?? false;

	const batchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const batchStartRef = useRef<number | null>(null);
	const pendingInvalidations = useRef<Set<string>>(new Set());

	useEffect(() => {
		return () => {
			if (batchTimerRef.current !== null) clearTimeout(batchTimerRef.current);
		};
	}, []);

	const getConnection = useCallback(
		async (forceRefresh?: boolean) => {
			try {
				const token = await getAccessTokenSilently(forceRefresh ? { cacheMode: "off" } : undefined);
				return {
					url: `${config.apiUrl}/v2/sse/subscribe`,
					headers: { Authorization: `Bearer ${token}` },
				};
			} catch (error) {
				if (isTerminalAuthenticationError(error)) throw new SseSessionEndedError();
				throw error;
			}
		},
		[getAccessTokenSilently, config.apiUrl],
	);

	const onInvalidate = useCallback(
		(queryKeys: string[][] | null) => {
			if (queryKeys === null) {
				if (batchTimerRef.current !== null) {
					clearTimeout(batchTimerRef.current);
					batchTimerRef.current = null;
				}
				batchStartRef.current = null;
				pendingInvalidations.current.clear();
				queryClient.invalidateQueries({ refetchType: "active" });
			} else {
				const now = Date.now();
				if (batchStartRef.current === null) batchStartRef.current = now;

				for (const queryKey of queryKeys) {
					pendingInvalidations.current.add(JSON.stringify(queryKey));
				}

				const elapsed = now - batchStartRef.current;
				const delay = Math.min(BATCH_DEBOUNCE_MS, Math.max(0, MAX_BATCH_WAIT_MS - elapsed));

				if (batchTimerRef.current !== null) clearTimeout(batchTimerRef.current);
				batchTimerRef.current = setTimeout(() => {
					batchTimerRef.current = null;
					batchStartRef.current = null;
					const pending = new Set(pendingInvalidations.current);
					pendingInvalidations.current.clear();
					queryClient.invalidateQueries({
						predicate: (query) => pending.has(JSON.stringify(query.queryKey)),
						refetchType: "active",
					});
				}, delay);
			}
		},
		[queryClient],
	);

	if (!sseEnabled || !user.setupComplete) return null;

	return <SSESubscriber getConnection={getConnection} onInvalidate={onInvalidate} />;
}
